import { createContext, useContext, useRef, useState } from 'react';
import { workspaceDirectoryStore } from '@/lib/assistants/directory';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import type { SidebarThread } from '@/lib/sidebar-threads';

/** Moves between conversations from views far from the shell's navigation. */
export type ConversationOpener = {
  /** Shows a conversation; false when it cannot be. */
  openConversation(id: string): Promise<boolean>;
};

export const ConversationOpenerContext = createContext<ConversationOpener | null>(null);

export function useConversationOpener(): ConversationOpener | null {
  return useContext(ConversationOpenerContext);
}

/** Whether `id` is the own conversation of one of the user's live projects. */
function isProjectConversation(id: string): boolean {
  return workspaceDirectoryStore()
    .getState()
    .projects.some((project) => project.conversationId === id && project.archivedAt === null);
}

/**
 * The shell's opener: stable for the views reading it, it uses the latest threads and navigation
 * when called. A project's own conversation the list does not hold (a new project's: no message
 * yet, so the listing leaves it out) opens empty under its id; its view loads what it has.
 */
export function useConversationOpenerValue(
  store: DesktopShellStore,
  threads: readonly SidebarThread[],
  navigation: {
    selectThread: (thread: SidebarThread) => Promise<boolean>;
    /** Shows a conversation the list does not hold, empty, under its id. */
    openEmpty: (id: string) => void;
  },
): ConversationOpener {
  const latest = useRef({ threads, navigation });
  latest.current = { threads, navigation };
  const [opener] = useState<ConversationOpener>(() => ({
    openConversation: async (id) => {
      if (id === store.getState().chatId) return true;
      const thread = latest.current.threads.find((candidate) => candidate.id === id);
      if (thread !== undefined) return await latest.current.navigation.selectThread(thread);
      if (!isProjectConversation(id)) return false;
      latest.current.navigation.openEmpty(id);
      return true;
    },
  }));
  return opener;
}
